import { authService } from './authService';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8000';

function getHeaders() {
  const token = authService.getToken();
  return {
    'Content-Type': 'application/json',
    ...(token && { Authorization: `Bearer ${token}` }),
  };
}

async function request(method, path, body) {
  const res = await fetch(`${API_BASE}${path}`, {
    method,
    headers: getHeaders(),
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });
  if (res.status === 401) {
    authService.logout();
    window.location.href = '/signin';
    throw new Error('Session expired. Please log in again.');
  }
  if (!res.ok) {
    const err = await res.json().catch(() => ({ detail: res.statusText }));
    throw new Error(err.detail || 'Request failed');
  }
  return res.json();
}

const get = (path) => request('GET', path);
const post = (path, body) => request('POST', path, body);
const put = (path, body) => request('PUT', path, body);
const patch = (path, body) => request('PATCH', path, body);
const del = (path) => request('DELETE', path);

// ===========================
// ROLE
// ===========================
export const getMyRole = () => get('/api/studio/my-role');
export const setRole = (role) => post('/api/studio/set-role', { role });

// ===========================
// SYLLABUS UPLOAD
// ===========================
export async function uploadSyllabus(file) {
  const token = authService.getToken();
  const formData = new FormData();
  formData.append('file', file);
  const res = await fetch(`${API_BASE}/api/studio/syllabus-upload`, {
    method: 'POST',
    headers: { ...(token && { Authorization: `Bearer ${token}` }) },
    body: formData,
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ detail: res.statusText }));
    throw new Error(err.detail || 'Upload failed');
  }
  return res.json();
}

// ===========================
// TEACHER: COURSES
// ===========================
export const createCourse = (data) => post('/api/studio/courses', data);
export const listCourses = () => get('/api/studio/courses');
export const getCourse = (courseId) => get(`/api/studio/courses/${courseId}`);
export const deleteCourse = (courseId) => del(`/api/studio/courses/${courseId}`);

export async function fetchCoursePdfBlob(courseId) {
  const token = authService.getToken();
  const res = await fetch(`${API_BASE}/api/studio/courses/${courseId}/syllabus-pdf`, {
    headers: { ...(token && { Authorization: `Bearer ${token}` }) },
  });
  if (!res.ok) throw new Error('PDF not available');
  return res.blob();
}

export const getTeacherChatHistory = (courseId) => get(`/api/studio/courses/${courseId}/teacher-chat`);
export const teacherChat = (data) => post('/api/studio/teacher-chat', data);
export const generateVideoFromPassage = (courseId, data) =>
  post(`/api/studio/courses/${courseId}/generate-video-from-passage`, data);

export const getTeacherDrafts = (courseId) => get(`/api/studio/teacher-drafts/${courseId}`);
export const saveTeacherDraft = (data) => post('/api/studio/teacher-drafts', data);
export const assignTeacherDraft = (draftId, data) => patch(`/api/studio/teacher-drafts/${draftId}`, data);
export const deleteTeacherDraft = (draftId) => del(`/api/studio/teacher-drafts/${draftId}`);

// ===========================
// TEACHER: CONCEPTS
// ===========================
export const extractConcepts = (courseId, body = {}) =>
  post(`/api/studio/courses/${courseId}/extract-concepts`, body);
export const updateConcept = (conceptId, data) =>
  put(`/api/studio/concepts/${conceptId}`, data);
export const deleteSyllabus = (courseId) => del(`/api/studio/courses/${courseId}/syllabus`);
export const reorderConcepts = (courseId, conceptIds) =>
  post(`/api/studio/courses/${courseId}/reorder-concepts`, { concept_ids: conceptIds });
export const deleteConcept = (conceptId) =>
  del(`/api/studio/concepts/${conceptId}`);
export const bulkDeleteConcepts = (conceptIds) =>
  post(`/api/studio/concepts/bulk-delete`, { concept_ids: conceptIds });
export const getConceptSource = (conceptId) =>
  get(`/api/studio/concepts/${conceptId}/source`);
export const previewConceptTranscript = (conceptId) =>
  post(`/api/studio/concepts/${conceptId}/preview-transcript`);
export const improveTranscriptSection = (conceptId, body) =>
  post(`/api/studio/concepts/${conceptId}/improve-transcript`, body);
export const addExampleToTranscript = (conceptId, body) =>
  post(`/api/studio/concepts/${conceptId}/add-example`, body);
export const generateConceptVideo = (conceptId, body = {}) =>
  post(`/api/studio/concepts/${conceptId}/generate-video`, body);
export const previewExtraTranscript = (conceptId, body) =>
  post(`/api/studio/concepts/${conceptId}/preview-extra-transcript`, body);
export const generateExtraConceptVideo = (conceptId, body) =>
  post(`/api/studio/concepts/${conceptId}/generate-extra-video`, body);
export const regenerateLinkedVideo = (conceptId, videoId) =>
  post(`/api/studio/concepts/${conceptId}/linked-videos/${videoId}/regenerate`);
export const generateConceptQuestions = (conceptId, body = {}) =>
  post(`/api/studio/concepts/${conceptId}/generate-questions`, body);
export const getConceptQuestions = (conceptId) =>
  get(`/api/studio/concepts/${conceptId}/questions`);

// ===========================
// TEACHER: QUESTIONS
// ===========================
export const createQuestion = (data) => post('/api/studio/questions', data);
export const updateQuestion = (questionId, data) =>
  put(`/api/studio/questions/${questionId}`, data);
export const deleteQuestion = (questionId) =>
  del(`/api/studio/questions/${questionId}`);

// ===========================
// TEACHER: ASSIGNMENTS
// ===========================
export const uploadAssignmentAttachment = async (file) => {
  const token = authService.getToken();
  const form = new FormData();
  form.append('file', file);
  const res = await fetch(`${API_BASE}/api/studio/assignments/upload-attachment`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}` },
    body: form,
  });
  if (!res.ok) { const e = await res.json().catch(() => ({})); throw new Error(e.detail || 'Upload failed'); }
  return res.json();
};
export const createAssignment = (data) => post('/api/studio/assignments', data);
export const listAssignments = (courseId) =>
  get(`/api/studio/assignments${courseId ? `?course_id=${courseId}` : ''}`);
export const getAssignment = (assignmentId) =>
  get(`/api/studio/assignments/${assignmentId}`);
export const updateAssignment = (assignmentId, data) =>
  put(`/api/studio/assignments/${assignmentId}`, data);
export const publishAssignment = (assignmentId) =>
  post(`/api/studio/assignments/${assignmentId}/publish`);
export const togglePublishAssignment = (assignmentId) =>
  post(`/api/studio/assignments/${assignmentId}/publish`);

// ===========================
// TEACHER: CLASS GROUPS
// ===========================
export const createClassGroup = (data) => post('/api/studio/class-groups', data);
export const listClassGroups = () => get('/api/studio/class-groups');
export const getClassStudents = (groupId) =>
  get(`/api/studio/class-groups/${groupId}/students`);

// ===========================
// TEACHER: ANALYTICS
// ===========================
export const getCourseAnalytics = (courseId) =>
  get(`/api/studio/analytics/course/${courseId}`);
export const getAssignmentAnalytics = (assignmentId) =>
  get(`/api/studio/analytics/assignment/${assignmentId}`);
export const getSubmissionDetail = (assignmentId, submissionId) =>
  get(`/api/studio/analytics/assignment/${assignmentId}/submissions/${submissionId}`);

// ===========================
// STUDENT: ENROLLMENT
// ===========================
export const enrollWithCode = (invite_code) =>
  post('/api/studio/enroll', { invite_code });
export const getMyCourses = () => get('/api/studio/my-courses');
export const getMyNotifications = () => get('/api/studio/my-notifications');
export const getMyProgress = (courseId) =>
  get(`/api/studio/courses/${courseId}/my-progress`);

// ===========================
// STUDENT: ASSIGNMENTS
// ===========================
export const getAssignmentForStudent = (assignmentId) =>
  get(`/api/studio/student/assignments/${assignmentId}`);
export const submitAssignment = (assignmentId, data) =>
  post(`/api/studio/student/assignments/${assignmentId}/submit`, data);
export const getMySubmission = (assignmentId, submissionId = null) =>
  get(`/api/studio/student/assignments/${assignmentId}/my-submission${submissionId ? `?submission_id=${submissionId}` : ''}`);

// ===========================
// STUDENT: ADAPTIVE PRACTICE
// ===========================
export const startAdaptivePractice = (conceptId) =>
  post(`/api/studio/concepts/${conceptId}/adaptive-practice`);
export const submitAdaptiveSession = (sessionId, data) =>
  post(`/api/studio/adaptive-sessions/${sessionId}/submit`, data);

// ===========================
// STUDENT: VIDEO PROGRESS
// ===========================
export const updateVideoProgress = (conceptId, watch_percentage) =>
  patch(`/api/studio/concepts/${conceptId}/video-progress`, { watch_percentage });

// ===========================
// CONCEPT MESSAGES (Student ↔ Teacher)
// ===========================
export const getTeacherSubmission = (submissionId) =>
  get(`/api/studio/teacher/submissions/${submissionId}`);
export const saveSubmissionComment = (submissionId, comment) =>
  patch(`/api/studio/teacher/submissions/${submissionId}/comment`, { comment });

export const getConceptMessages = (conceptId, studentId = null) =>
  get(`/api/studio/concepts/${conceptId}/messages${studentId ? `?student_id=${studentId}` : ''}`);
export const sendConceptMessage = (conceptId, body, studentId = null) =>
  post(`/api/studio/concepts/${conceptId}/messages`, { body, ...(studentId && { student_id: studentId }) });

// ===========================
// TEACHER: STUDENT ROSTER
// ===========================
export const listTeacherStudents = () => get('/api/studio/teacher/students');
export const getStudentDetail = (studentId) => get(`/api/studio/teacher/students/${studentId}`);

// ===========================
// STUDENT: CONVERSATIONAL PRACTICE
// ===========================
export const startPractice = (conceptId) =>
  post(`/api/studio/concepts/${conceptId}/practice-start`);
export const submitPracticeAnswer = (sessionId, data) =>
  post(`/api/studio/practice-sessions/${sessionId}/answer`, data);

// ===========================
// TEACHER: VIDEO LIBRARY
// ===========================
export const getMyVideos = () => get('/api/studio/my-videos');
export const getConceptVideos = (conceptId) => get(`/api/studio/concepts/${conceptId}/videos`);
export const getConceptTranscript = (conceptId) => get(`/api/studio/concepts/${conceptId}/transcript`);
export const getVideoTranscript = (videoId) => get(`/api/studio/videos/${videoId}/transcript`);
export const addConceptVideo = (conceptId, videoId) =>
  post(`/api/studio/concepts/${conceptId}/videos/${videoId}`);
export const removeConceptVideo = (conceptId, videoId) =>
  del(`/api/studio/concepts/${conceptId}/videos/${videoId}`);

// ===========================
// STUDENT: QUESTION FLAGS
// ===========================
export const toggleQuestionFlag = (questionId) =>
  post(`/api/studio/questions/${questionId}/flag`);
export const getMyFlags = (conceptId = null) =>
  get(`/api/studio/my-flags${conceptId ? `?concept_id=${conceptId}` : ''}`);

// ===========================
// TEACHER: ENROLLED STUDENTS PER COURSE
// ===========================
export const getCourseStudents = (courseId) =>
  get(`/api/studio/courses/${courseId}/enrolled-students`);

// ===========================
// TEACHER: AI SESSION MONITORING
// ===========================
export const getStudentAiSessions = (studentId) =>
  get(`/api/studio/teacher/students/${studentId}/ai-sessions`);
export const getStudentAiSession = (studentId, sessionId) =>
  get(`/api/studio/teacher/students/${studentId}/ai-sessions/${sessionId}`);

// ===========================
// STUDENT: KNOWLEDGE PROFILE
// ===========================
export const getStudentProfile = () => get('/api/studio/student/profile');
export const updateStudentProfile = (knowledge_level) =>
  put('/api/studio/student/profile', { knowledge_level });

// ===========================
// STUDENT: STUDY SETS
// ===========================
export async function uploadStudySet(file, title) {
  const token = authService.getToken();
  const formData = new FormData();
  formData.append('file', file);
  formData.append('title', title);
  const res = await fetch(`${API_BASE}/api/studio/student/study-sets/upload`, {
    method: 'POST',
    headers: { ...(token && { Authorization: `Bearer ${token}` }) },
    body: formData,
  });
  if (res.status === 401) { authService.logout(); window.location.href = '/signin'; }
  if (!res.ok) {
    const err = await res.json().catch(() => ({ detail: res.statusText }));
    throw new Error(err.detail || 'Upload failed');
  }
  return res.json();
}
export const extractStudyConcepts = (id) =>
  post(`/api/studio/student/study-sets/${id}/extract-concepts`);
export const listStudySets = () => get('/api/studio/student/study-sets');
export const getStudySet = (id) => get(`/api/studio/student/study-sets/${id}`);
export const deleteStudySet = (id) => del(`/api/studio/student/study-sets/${id}`);
export async function uploadSelectionImage(studySetId, blob) {
  const token = authService.getToken();
  const form = new FormData();
  form.append('file', blob, 'selection.png');
  const res = await fetch(`${API_BASE}/api/studio/student/study-sets/${studySetId}/upload-selection-image`, {
    method: 'POST',
    headers: { ...(token && { Authorization: `Bearer ${token}` }) },
    body: form,
  });
  if (!res.ok) { const e = await res.json().catch(() => ({})); throw new Error(e.detail || 'Upload failed'); }
  return res.json(); // { image_url }
}

export async function fetchStudySetPdfBlob(id) {
  const token = authService.getToken();
  const res = await fetch(`${API_BASE}/api/studio/student/study-sets/${id}/pdf`, {
    headers: { ...(token && { Authorization: `Bearer ${token}` }) },
  });
  if (!res.ok) throw new Error('PDF not available');
  return res.blob();
}

// ===========================
// STUDENT: SESSION HISTORY
// ===========================
export const getStudentHistory = () => get('/api/studio/student/history');
